import { useEffect, useState } from 'react';
import { Banner, Button, Card, Dialog, Field, PageHeader, Pill } from '@elysian/ui';
import type { VatCode, VatCodeList } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { iso, longDate, shortDate } from '../invoices/shared';

type Applies = VatCode['applies_to'];
type Kind = VatCode['kind'];

const FOR: Record<Applies, string> = { sales: 'Sales', purchases: 'Purchases', both: 'Sales and purchases' };
const KINDS: { value: Kind; label: string }[] = [
  { value: 'standard', label: 'Standard rate' },
  { value: 'reduced', label: 'Reduced rate' },
  { value: 'second_reduced', label: 'Second reduced rate' },
  { value: 'livestock', label: 'Livestock rate' },
  { value: 'zero', label: 'Zero rated' },
  { value: 'exempt', label: 'Exempt' },
  { value: 'outside_scope', label: 'Outside the scope of VAT' },
  { value: 'reverse_charge', label: 'Reverse charge' },
  { value: 'intra_eu_goods', label: 'EU goods' },
  { value: 'intra_eu_services', label: 'EU services' },
  { value: 'import_postponed', label: 'Imports, postponed accounting' },
];
const kindLabel = (k: Kind) => KINDS.find((x) => x.value === k)?.label ?? k;
const pct = (r: string | null | undefined) => (r == null ? '—' : `${Number(r).toString()}%`);
const ZERO_KINDS: Kind[] = ['zero', 'exempt', 'outside_scope'];

/** The boxes a new code usually feeds, as ledger._vat_default_boxes. */
function suggestedBoxes(list: VatCodeList, kind: Kind, applies: Applies, rate: number): string[] {
  if (list.return_type === 'IE_VAT3') {
    return rate > 0 && ['standard', 'reduced', 'second_reduced', 'livestock'].includes(kind)
      ? [applies !== 'purchases' && 'T1', applies !== 'sales' && 'T2'].filter(Boolean) as string[] : [];
  }
  if (kind === 'outside_scope') return [];
  return [applies !== 'purchases' && rate > 0 && '1', applies !== 'sales' && rate > 0 && '4', applies !== 'purchases' && '6', applies !== 'sales' && '7']
    .filter(Boolean) as string[];
}

/**
 * Settings → VAT codes: each code with its rate, what it's for and the return
 * boxes it feeds. A rate changes from a day (posted entries keep theirs); a
 * code that's been used is switched off, never deleted.
 */
export function VatCodes() {
  const { source, can, today } = useSession();
  const toast = useToast((t) => t.show);
  const now = iso(today());
  const data = useAsync(() => source.vatCodes(now), [source, now]);
  const [list, setList] = useState<VatCodeList | null>(null);
  const [showOff, setShowOff] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const editable = can('settings.accounts');

  useEffect(() => { if (data.state === 'ready') setList(data.data); }, [data]);

  const off = list?.codes.filter((c) => !c.is_active).length ?? 0;
  const shown = list?.codes.filter((c) => c.is_active || showOff) ?? [];
  const boxLabel = (b: string) => list?.boxes.find((x) => x.box === b)?.label ?? b;
  const returnName = list?.return_type === 'UK_VAT100' ? 'VAT 100' : 'VAT3';
  const current = list?.codes.find((c) => c.code === open) ?? null;

  return (
    <>
      <PageHeader title="VAT codes"
        subtitle={`The rates on invoices, bills and journals, and the ${returnName} boxes each one feeds.`}
        actions={editable && list ? <Button variant="primary" onClick={() => setAdding(true)}>Add a VAT code</Button> : undefined} />
      {data.state === 'error' && <Banner>Couldn’t load the VAT codes: {data.error.message}</Banner>}
      {data.state === 'loading' && !list && <div className="el-skeleton" style={{ height: 240 }} aria-label="Loading the VAT codes" />}
      {list && (
        <Card as="section" padding="16px 0 8px" aria-labelledby="vat-codes-h">
          <div className="el-card-head" style={{ flexWrap: 'wrap' }}>
            <h2 id="vat-codes-h" className="el-h2">{shown.length} codes{showOff ? '' : ' switched on'}</h2>
            {off > 0 && (
              <label className="el-check" style={{ minHeight: 0 }}>
                <input type="checkbox" checked={showOff} onChange={(e) => setShowOff(e.target.checked)} />
                Show switched-off codes ({off})
              </label>
            )}
          </div>
          <div className="el-table-wrap">
            <table className="el-table" aria-label="VAT codes">
              <thead>
                <tr><th scope="col">Code</th><th scope="col">Name</th><th scope="col">For</th><th scope="col" className="el-num">Rate today</th>
                  <th scope="col">{returnName} boxes</th><th scope="col"><span className="el-vh">Status</span></th></tr>
              </thead>
              <tbody>
                {shown.map((c) => (
                  <tr key={c.code}>
                    <td><button type="button" className="el-linkbtn" onClick={() => setOpen(c.code)} aria-label={`${c.code}: ${c.name}`}>{c.code}</button></td>
                    <td>{c.name}{c.is_reverse_charge && <div className="el-hint">{kindLabel(c.kind)}</div>}</td>
                    <td>{FOR[c.applies_to]}</td>
                    <td className="el-num">{pct(c.rate)}
                      {c.next_change && <div className="el-hint">{pct(c.next_change.rate)} from {shortDate(c.next_change.from)}</div>}</td>
                    <td>{c.boxes.length ? c.boxes.map((b) => <span key={b} title={boxLabel(b)} style={{ marginRight: 4 }}><Pill tone="info">{b}</Pill></span>)
                      : <span className="el-hint">None</span>}</td>
                    <td>{!c.is_active ? <Pill tone="draft">Switched off</Pill> : c.in_use ? <span className="el-hint">Used</span> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="el-hint" style={{ margin: '12px 20px 4px' }}>
            {list.last_filed_to ? `Returns are filed to ${longDate(list.last_filed_to)}: a new rate can start after that. ` : ''}
            Posted invoices and bills always keep the rate they were posted at.
          </p>
        </Card>
      )}
      {current && list && (
        <CodeDialog code={current} list={list} editable={editable} now={now} onClose={() => setOpen(null)}
          onChanged={(next, done) => { setList(next); toast(done); }} />
      )}
      {adding && list && (
        <AddDialog list={list} onClose={() => setAdding(false)}
          onAdded={(next, code) => { setList(next); setAdding(false); toast(`${code} added`); }} />
      )}
    </>
  );
}

function BoxChoices({ list, value, onChange, idp, disabled }: { list: VatCodeList; value: string[]; onChange: (b: string[]) => void; idp: string; disabled?: boolean }) {
  return (
    <fieldset style={{ border: 0, padding: 0, margin: 0 }} disabled={disabled}>
      <legend className="el-label">Feeds these {list.return_type === 'UK_VAT100' ? 'VAT 100' : 'VAT3'} boxes</legend>
      <div className="el-check-grid">
        {list.boxes.map((b) => (
          <label key={b.box} className="el-check">
            <input type="checkbox" name={`${idp}-box`} checked={value.includes(b.box)}
              onChange={(e) => onChange(e.target.checked ? [...value, b.box] : value.filter((x) => x !== b.box))} />
            <span>{b.label}<span className="el-hint" style={{ display: 'block' }}>{b.measure === 'tax' ? 'The VAT' : 'The net amount'}, {b.side === 'output' ? 'sales side' : 'purchases side'}</span></span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function AddDialog({ list, onClose, onAdded }: { list: VatCodeList; onClose: () => void; onAdded: (next: VatCodeList, code: string) => void }) {
  const { source } = useSession();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<Kind>('standard');
  const [rate, setRate] = useState('');
  const [applies, setApplies] = useState<Applies>('both');
  const [boxes, setBoxes] = useState<string[] | null>(null);   // null: follow the suggestion
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rateNum = Number(rate.replace('%', '')) || 0;
  const chosen = boxes ?? suggestedBoxes(list, kind, applies, rateNum);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      const next = await source.createVatCode({ code: code.trim(), name: name.trim(), kind, rate: rate.trim() || '0', applies_to: applies, boxes: chosen });
      onAdded(next, code.trim().toUpperCase());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title="Add a VAT code" onClose={onClose}
      actions={<><Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={busy || !code.trim() || !name.trim()} onClick={() => void add()}>{busy ? 'Adding…' : 'Add the code'}</Button></>}>
      <div className="el-form-grid">
        <Field id="vc-code" label="Code" hint="Letters and digits, such as R48">
          <input id="vc-code" className="el-input" value={code} maxLength={10} onChange={(e) => setCode(e.target.value.toUpperCase())} />
        </Field>
        <Field id="vc-rate" label="Rate (%)">
          <input id="vc-rate" className="el-input el-num" inputMode="decimal" value={ZERO_KINDS.includes(kind) ? '0' : rate}
            disabled={ZERO_KINDS.includes(kind)} onChange={(e) => setRate(e.target.value)} />
        </Field>
      </div>
      <Field id="vc-name" label="Name" hint="As it shows in the VAT picker on invoices and bills">
        <input id="vc-name" className="el-input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="el-form-grid">
        <Field id="vc-kind" label="Kind">
          <select id="vc-kind" className="el-input" value={kind} onChange={(e) => { setKind(e.target.value as Kind); if (ZERO_KINDS.includes(e.target.value as Kind)) setRate('0'); }}>
            {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </Field>
        <Field id="vc-for" label="Used on">
          <select id="vc-for" className="el-input" value={applies} onChange={(e) => setApplies(e.target.value as Applies)}>
            <option value="both">Sales and purchases</option><option value="sales">Sales only</option><option value="purchases">Purchases only</option>
          </select>
        </Field>
      </div>
      <BoxChoices list={list} value={chosen} onChange={setBoxes} idp="vc-new" />
      {boxes === null && <p className="el-hint" style={{ margin: 0 }}>Boxes suggested from the kind, rate and use. Ask your accountant if unsure.</p>}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}

function CodeDialog({ code: c, list, editable, now, onClose, onChanged }: {
  code: VatCode; list: VatCodeList; editable: boolean; now: string; onClose: () => void; onChanged: (next: VatCodeList, done: string) => void;
}) {
  const { source } = useSession();
  const [name, setName] = useState(c.name);
  const [applies, setApplies] = useState<Applies>(c.applies_to);
  const [boxes, setBoxes] = useState<string[]>(c.boxes);
  const [rate, setRate] = useState('');
  const [from, setFrom] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const dirty = name.trim() !== c.name || applies !== c.applies_to || [...boxes].sort().join() !== [...c.boxes].sort().join();
  const earliest = [list.last_filed_to, c.used_until].filter(Boolean).sort().pop();
  const minFrom = earliest ? nextDay(earliest) : undefined;

  async function run(fn: () => Promise<VatCodeList>, done: string, close = false) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await fn();
      onChanged(next, done);
      if (next.drafts_to_resave) {
        setNotice(`${next.drafts_to_resave} draft${next.drafts_to_resave === 1 ? '' : 's'} dated from then still ${next.drafts_to_resave === 1 ? 'has' : 'have'} the old rate: open and save ${next.drafts_to_resave === 1 ? 'it' : 'them'} to update.`);
      }
      if (close) onClose();
      setRate('');
      setFrom('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={`${c.code}: ${c.name}`} onClose={onClose} actions={<Button onClick={onClose}>Close</Button>}>
      <p className="el-hint" style={{ margin: 0 }}>
        {kindLabel(c.kind)} · {c.is_active ? 'switched on' : 'switched off'}
        {c.used_until ? ` · last used ${longDate(c.used_until)}` : c.in_use ? ' · used on drafts' : ' · not used yet'}
      </p>

      <section aria-labelledby="vc-rates-h" className="el-stack" style={{ gap: 8 }}>
        <h3 id="vc-rates-h" className="el-label" style={{ margin: 0, fontSize: 14 }}>Rates</h3>
        <table className="el-table" aria-label={`Rates for ${c.code}`}>
          <thead><tr><th scope="col" className="el-num">Rate</th><th scope="col">From</th><th scope="col">Until</th><th scope="col"><span className="el-vh">Actions</span></th></tr></thead>
          <tbody>
            {c.rates.map((r, i) => (
              <tr key={r.from}>
                <td className="el-num">{pct(r.rate)}</td>
                <td>{r.from <= '2000-01-01' ? 'The start' : shortDate(r.from)}</td>
                <td>{r.to ? shortDate(lastDay(r.to)) : r.from > now ? 'Onwards' : 'Now'}</td>
                <td>{editable && !r.used && i < c.rates.length - 1 && r.from > (earliest ?? '') && (
                  <button type="button" className="el-linkbtn" disabled={busy}
                    onClick={() => void run(() => source.removeVatRate(c.code, r.from), `The ${pct(r.rate)} rate from ${shortDate(r.from)} is taken back`)}>Remove</button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {editable && (
          <form noValidate className="el-row" style={{ gap: 8, alignItems: 'flex-end' }}
            onSubmit={(e) => { e.preventDefault(); void run(() => source.setVatRate(c.code, rate, from), `${c.code}: ${pct(rate)} from ${shortDate(from)}`); }}>
            <Field id="vc-new-rate" label="New rate (%)">
              <input id="vc-new-rate" className="el-input el-num" inputMode="decimal" style={{ width: 110 }} value={rate} onChange={(e) => setRate(e.target.value)} />
            </Field>
            <Field id="vc-new-from" label="Starting on">
              <input id="vc-new-from" type="date" className="el-input" style={{ width: 'auto' }} min={minFrom} value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Button type="submit" disabled={busy || !rate.trim() || !from}>Set the rate</Button>
          </form>
        )}
        {minFrom && editable && <p className="el-hint" style={{ margin: 0 }}>A new rate can start on {longDate(minFrom)} at the earliest: entries before then keep their rate.</p>}
        {notice && <Banner tone="info">{notice}</Banner>}
      </section>

      <section aria-labelledby="vc-details-h" className="el-stack" style={{ gap: 12 }}>
        <h3 id="vc-details-h" className="el-label" style={{ margin: 0, fontSize: 14 }}>Details</h3>
        <Field id="vc-edit-name" label="Name">
          <input id="vc-edit-name" className="el-input" value={name} disabled={!editable} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field id="vc-edit-for" label="Used on">
          <select id="vc-edit-for" className="el-input" value={applies} disabled={!editable} onChange={(e) => setApplies(e.target.value as Applies)}>
            <option value="both">Sales and purchases</option><option value="sales">Sales only</option><option value="purchases">Purchases only</option>
          </select>
        </Field>
        <BoxChoices list={list} value={boxes} onChange={setBoxes} idp="vc-edit" disabled={!editable} />
        {editable && dirty && c.in_use && (
          <p className="el-hint" style={{ margin: 0 }}>New boxes apply to returns not yet filed; filed returns keep their figures.</p>
        )}
        {editable && (
          <div className="el-row">
            <Button variant="primary" disabled={busy || !dirty || !name.trim()}
              onClick={() => void run(() => source.updateVatCode(c.code, { name: name.trim(), applies_to: applies, boxes }), `${c.code} saved`)}>Save details</Button>
          </div>
        )}
      </section>

      {editable && (
        <section aria-label="Switch off or delete" className="el-row" style={{ gap: 8, borderTop: '1px solid var(--el-rule)', paddingTop: 12 }}>
          <Button disabled={busy} onClick={() => void run(() => source.setVatCodeActive(c.code, !c.is_active), c.is_active ? `${c.code} switched off` : `${c.code} switched on`)}>
            {c.is_active ? 'Switch off' : 'Switch back on'}
          </Button>
          {!c.in_use && (
            <Button variant="danger" disabled={busy} onClick={() => void run(() => source.deleteVatCode(c.code), `${c.code} deleted`, true)}>Delete</Button>
          )}
          <span className="el-hint" style={{ flex: '1 1 220px' }}>
            {c.in_use ? 'Used codes stay in the books. Switched off, a code is no longer offered on new entries.' : 'Not used yet, so it can be deleted.'}
          </span>
        </section>
      )}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}

function nextDay(d: string) { const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + 1); return x.toISOString().slice(0, 10); }
function lastDay(exclusiveTo: string) { const x = new Date(`${exclusiveTo}T12:00:00Z`); x.setUTCDate(x.getUTCDate() - 1); return x.toISOString().slice(0, 10); }
